import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { Suits, DealSeats } from './BridgeTypes';
import { DeckStateContext } from './BridgeEngineDeck';
import { Deal } from './BridgeEnginePlay';
import { preProcessFile } from 'typescript';

// JSON format.
interface HandJson {
    [Suits.Spades]: string;
    [Suits.Hearts]: string;
    [Suits.Diamonds]: string;
    [Suits.Clubs]: string;
}

enum DealSeatJson {
    North = 'north',
    South = 'south',
    East = 'east',
    West = 'west'
}
interface DealJson {
    [DealSeatJson.North]: HandJson;
    [DealSeatJson.South]: HandJson;
    [DealSeatJson.East]: HandJson;
    [DealSeatJson.West]: HandJson;
}
export interface AuctionNotes {
    level: SkillLevel;
    bid: string;
    notes: string[];
}
export interface BoardJson {
    boardId: string;
    dealerSeat: DealSeats;
    vulnerableSeats: DealSeats[];
    deal: DealJson;
    auction: string[];
    auctionNotes: AuctionNotes[];
    lead: string;
    hints: string[][];
}
interface BoardsJson {
    boards: BoardJson[];
}

// GameSettingsContext.
// in the dropdown, I exclude 'worldclass' as a teaser.
export enum SkillLevel {
    Beginner = 'beginner',
    Intermediate = 'intermediate',
    Advanced = 'advanced',
    LifeMaster = 'lifemaster',
    Expert = 'expert',
    WorldClass = 'worldclass',
}

export interface GameSettingsState {
    allBoards: BoardJson[];
    skillLevel: SkillLevel;

    tenAsT: boolean;

    maxTurns: number;
    showSuitDepth: boolean;
    honorsOnly: boolean;
    tenIsHonor: boolean;
    guessMarksCorrect: boolean;

    fixedPlayerSeat: DealSeats | null;
    oneshotBoard: string | null;
    oneshotPlayerSeat: DealSeats | null;
}

export interface GameSettingsContextValue {
    gameSettings: GameSettingsState;
    updateGameSettings: (newSettings: Partial<GameSettingsState>) => void;
    doesBoardExist: (boardId: string) => boolean;
    transformDealJsonToDeal: (dealJson: DealJson) => Deal;
}

const initialGameSettingsContextValue: GameSettingsContextValue = {
    gameSettings: {
        allBoards: [],
        skillLevel: SkillLevel.Intermediate,

        tenAsT: false,

        maxTurns: 2,
        showSuitDepth: false,
        honorsOnly: false,
        tenIsHonor: false,
        guessMarksCorrect: false,

        fixedPlayerSeat: null,
        oneshotBoard: null,
        oneshotPlayerSeat: null,
    },
    updateGameSettings: () => {},
    doesBoardExist: () => false,
    transformDealJsonToDeal: () => ({ north: [], south: [], east: [], west: [] }),
};

export const GameSettingsContext = createContext<GameSettingsContextValue>(initialGameSettingsContextValue);
export const GameSettingsContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const deckStateContext = useContext(DeckStateContext);
    const { initialDeck } = deckStateContext;
    const [gameSettings, setGameSettings] = useState<GameSettingsState>(initialGameSettingsContextValue.gameSettings);

    useEffect(() => {
        loadGames();
    }, []);

    useEffect(() => {
        updateSettingsForSkillLevel(gameSettings.skillLevel);
    }, [gameSettings.skillLevel]);

    const updateGameSettings = (newSettings: Partial<GameSettingsState>): void => {
        setGameSettings((prevSettings) => ({ ...prevSettings, ...newSettings }));
    };

    // load the games from the JSON.
    const loadGames = async () => {
        try {
            const response = await fetch('/suited/BridgeGames.json');
            let boardsJson: BoardsJson = await response.json();
            boardsJson = validateBoards(boardsJson);
            updateGameSettings({ allBoards: boardsJson.boards });
            console.log("Loaded games: ", boardsJson.boards.length);
        } catch (error) {
            console.error("Error loading games: ", error);
        }
    }

    const validateBoards = (boardsJson: BoardsJson): BoardsJson => {
        const validBoards = boardsJson.boards.filter((board) => {
            const deal: Deal = transformDealJsonToDeal(board.deal);

            const allCards = [...deal.north, ...deal.south, ...deal.east, ...deal.west];
            const cardCount: { [key: string]: number } = {};

            if (allCards.length !== 52) {
                console.error(`Board ${board.boardId} is invalid: Expected 52 cards, but got ${allCards.length}`);
                return false;
            }

            // maps out the cards.
            allCards.forEach(card => {
                cardCount[card] = (cardCount[card] || 0) + 1;
            });

            const duplicateCards = Object.keys(cardCount).filter(card => cardCount[card] > 1);
            const missingCards = initialDeck().map(card => card.id).filter(card => !cardCount[card]);

            if (duplicateCards.length > 0 || missingCards.length > 0) {
                console.error(`Board ${board.boardId} is invalid:`);
                if (duplicateCards.length > 0) {
                    console.error(`Duplicate cards: ${duplicateCards.join(', ')}`);
                }
                if (missingCards.length > 0) {
                    console.error(`Missing cards: ${missingCards.join(', ')}`);
                }
                return false;
            }

            return true;
        });

        return { boards: validBoards };
    }

    const transformDealJsonToDeal = (dealJson: DealJson): Deal => {
        const deal: Deal = {
            north: [],
            south: [],
            east: [],
            west: []
        };

        Object.values(DealSeatJson).forEach((seat: DealSeatJson) => {
            const hand = dealJson[seat];
            Object.values(Suits).forEach((suit: Suits) => {
                const cards = hand[suit];
                for (const card of cards) {
                    deal[seat].push(`${card.toUpperCase()}${suit.charAt(0).toLowerCase()}`);
                }
            });
        });

        return deal;
    };

    const doesBoardExist = (boardId: string): boolean => {
        const board = gameSettings.allBoards.find(board => board.boardId === boardId);
        if (!board) {
            return false;
        }
        return true;
    }

    const updateSettingsForSkillLevel = (skillLevel: SkillLevel): void => {
        setGameSettings((prevSettings) => {
            const newGameSettings = { ...prevSettings };
            switch (skillLevel) {
                case SkillLevel.Beginner:
                    newGameSettings.maxTurns = 13;
                    newGameSettings.showSuitDepth = true;
                    newGameSettings.honorsOnly = true;
                    newGameSettings.tenIsHonor = false;
                    newGameSettings.guessMarksCorrect = true;
                    break;
                case SkillLevel.Intermediate:
                    newGameSettings.maxTurns = 10;
                    newGameSettings.showSuitDepth = true;
                    newGameSettings.honorsOnly = true;
                    newGameSettings.tenIsHonor = true;
                    newGameSettings.guessMarksCorrect = true;
                    break;
                case SkillLevel.Advanced:
                    newGameSettings.maxTurns = 10;
                    newGameSettings.showSuitDepth = false;
                    newGameSettings.honorsOnly = true;
                    newGameSettings.tenIsHonor = true;
                    newGameSettings.guessMarksCorrect = false;
                    break;
                case SkillLevel.LifeMaster:
                    newGameSettings.maxTurns = 10;
                    newGameSettings.showSuitDepth = false;
                    newGameSettings.honorsOnly = false;
                    newGameSettings.tenIsHonor = true;
                    newGameSettings.guessMarksCorrect = false;
                    break;
                case SkillLevel.Expert:
                    newGameSettings.maxTurns = 8;
                    newGameSettings.showSuitDepth = false;
                    newGameSettings.honorsOnly = false;
                    newGameSettings.tenIsHonor = true;
                    newGameSettings.guessMarksCorrect = false;
                    break;
                case SkillLevel.WorldClass:
                    newGameSettings.maxTurns = 6;
                    newGameSettings.showSuitDepth = false;
                    newGameSettings.honorsOnly = false;
                    newGameSettings.tenIsHonor = true;
                    newGameSettings.guessMarksCorrect = false;
                    break;
                default:
                    break;
            }
            return (newGameSettings);
        });
    };

    const contextValue: GameSettingsContextValue = {
        gameSettings,
        updateGameSettings,
        doesBoardExist,
        transformDealJsonToDeal,
    };

    return (
        <GameSettingsContext.Provider value={contextValue}>
            {children}
        </GameSettingsContext.Provider>
    );
};

export default GameSettingsContextProvider;
